import React from 'react';
import { Box, Typography, Paper, Chip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PendingIcon from '@mui/icons-material/Pending';
import BlockIcon from '@mui/icons-material/Block';
import { Plan } from '../types/generated/graphql';
import { colors } from '../theme/colors';
import { PlanProgress } from '../utils/taskTree';

interface PlanSummaryCardProps {
  plan: Plan;
  planProgress: PlanProgress;
}

export const PlanSummaryCard: React.FC<PlanSummaryCardProps> = ({
  plan,
  planProgress,
}) => {
  const { inProgress, blocked } = planProgress;
  const hasSummary = !!plan.summary;
  const hasStatusBreakdown = inProgress > 0 || blocked > 0;

  // Don't render if nothing to show
  if (!hasSummary && !hasStatusBreakdown) {
    return null;
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        bgcolor: colors.gray[50],
        border: `1px solid ${colors.gray[200]}`,
        borderRadius: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <InfoOutlinedIcon sx={{ fontSize: 18, color: colors.textSecondary }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: colors.textPrimary }}
        >
          About this plan
        </Typography>
      </Box>

      {/* Summary text */}
      {hasSummary && (
        <Typography
          variant="body2"
          sx={{
            color: colors.textSecondary,
            lineHeight: 1.6,
            mb: hasStatusBreakdown ? 2 : 0,
          }}
        >
          {plan.summary}
        </Typography>
      )}

      {/* Status breakdown chips */}
      {hasStatusBreakdown && (
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {inProgress > 0 && (
            <Chip
              icon={<PendingIcon sx={{ fontSize: 14 }} />}
              label={`${inProgress} in progress`}
              size="small"
              sx={{
                bgcolor: `${colors.amber[500]}15`,
                color: colors.amber[500],
                border: `1px solid ${colors.amber[500]}30`,
                fontSize: '0.75rem',
                '& .MuiChip-icon': { color: colors.amber[500] },
              }}
            />
          )}
          {blocked > 0 && (
            <Chip
              icon={<BlockIcon sx={{ fontSize: 14 }} />}
              label={`${blocked} blocked`}
              size="small"
              sx={{
                bgcolor: `${colors.red[600]}15`,
                color: colors.red[600],
                border: `1px solid ${colors.red[600]}30`,
                fontSize: '0.75rem',
                '& .MuiChip-icon': { color: colors.red[600] },
              }}
            />
          )}
        </Box>
      )}
    </Paper>
  );
};

export default PlanSummaryCard;
